import { intl } from '@shared/localization';
import { toastService } from '@toasts';

import {
  ALLOWED_EXTENSIONS,
  MAX_ATTACHMENT_COUNT,
  MAX_EMAIL_SIZE_BYTES,
  MAX_EMAIL_SIZE_MB,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
  MAX_MBFILE_SIZE_BYTES,
  MAX_MBFILE_SIZE_MB,
} from '../../constants';

export const getExtension = (fileName: string) =>
  fileName.split('.').pop()?.toLowerCase() || '';

export const hasFiles = (dataTransfer: DataTransfer | null) =>
  !!dataTransfer?.types && Array.from(dataTransfer.types).includes('Files');

const isSameFile = (leftFile: File, rightFile: File) =>
  leftFile.name === rightFile.name &&
  leftFile.lastModified === rightFile.lastModified &&
  leftFile.size === rightFile.size;

export const validateFiles = (
  files: File[],
  fileList: FileList | null,
  isMultiple: boolean = true,
) => {
  if (!fileList) return [];
  const maxFileSizBytes = isMultiple
    ? MAX_FILE_SIZE_BYTES
    : MAX_MBFILE_SIZE_BYTES;
  const maxFileSizMB = isMultiple ? MAX_FILE_SIZE_MB : MAX_MBFILE_SIZE_MB;

  const incomingFiles = Array.from(fileList);
  const acceptedFiles: File[] = [];
  const errors: string[] = [];
  let currentTotalSize = files.reduce((sum, file) => sum + file.size, 0);
  let filesCount = files.length;

  incomingFiles.forEach(file => {
    if (filesCount + 1 > MAX_ATTACHMENT_COUNT) {
      errors.push(
        intl.formatMessage(
          { id: 'messaging.attachment.errorMaxFiles' },
          {
            fileName: file.name,
            maxAttachmentCount: MAX_ATTACHMENT_COUNT,
          },
        ),
      );

      return;
    }

    if (
      files.some(existingFile => isSameFile(existingFile, file)) ||
      acceptedFiles.some(acceptedFile => isSameFile(acceptedFile, file))
    ) {
      errors.push(
        intl.formatMessage(
          { id: 'messaging.attachment.errorDuplicate' },
          { fileName: file.name },
        ),
      );

      return;
    }

    const extension = getExtension(file.name);

    if (!ALLOWED_EXTENSIONS.has(extension)) {
      errors.push(
        intl.formatMessage(
          { id: 'messaging.attachment.errorNotAllowed' },
          { fileName: file.name },
        ),
      );

      return;
    }

    if (file.size > maxFileSizBytes) {
      errors.push(
        intl.formatMessage(
          { id: 'messaging.attachment.errorFileSize' },
          {
            fileName: file.name,
            maxFileSize: maxFileSizMB,
          },
        ),
      );
      return;
    }

    if (currentTotalSize + file.size > MAX_EMAIL_SIZE_BYTES) {
      errors.push(
        intl.formatMessage(
          { id: 'messaging.attachment.errorTotalSize' },
          {
            fileName: file.name,
            maxTotalSize: MAX_EMAIL_SIZE_MB,
          },
        ),
      );

      return;
    }

    acceptedFiles.push(file);
    currentTotalSize += file.size;
    filesCount += 1;
  });

  if (errors.length > 0) {
    toastService.error(errors);
  }

  return acceptedFiles;
};
